'use client';

import { useEffect, useRef } from 'react';
import { CLIENT_TOKEN_FIELD, createClientToken, pickHoneypotField } from '../spam-guard';

/** Off-screen, out of the tab order and hidden from the accessibility tree, so
 *  neither pointer, keyboard nor screen-reader users can reach it — only bots. */
const HONEYPOT_CLASS = 'pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0';

/** Honeypot and JS token from spam-guard.ts; the third signal, the fill duration,
 *  is stamped on submit by useFillDuration().
 *
 *  Both values are random, so they can't be rendered — server and client markup
 *  would differ. The effect writes them into the DOM instead, which is what an
 *  effect is for. A bot that runs no JS therefore submits an empty token, which
 *  detectSpam() rejects. */
export function FormSpamGuard() {
    const honeypotRef = useRef<HTMLInputElement>(null);
    const tokenRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Nameless until here, so the served HTML never reveals the trap's name.
        if (honeypotRef.current) honeypotRef.current.name = pickHoneypotField();
        // defaultValue over value: it survives the form.reset() after a send.
        if (tokenRef.current) tokenRef.current.defaultValue = createClientToken();
    }, []);

    return (
        <>
            <input
                ref={honeypotRef}
                type="text"
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
                className={HONEYPOT_CLASS}
            />
            <input ref={tokenRef} type="hidden" name={CLIENT_TOKEN_FIELD} />
        </>
    );
}
